import { logoutState } from '@/app/_auth/logout-state'
import { dropdownItemClass } from '@/components/navbar-next/'
import { useUploadableDocumentTypes } from '@/helpers/permission-helpers'
import { useAuthContext } from '@/hooks/use-auth-context'
import { Button, Loader, Menu } from '@mantine/core'
import { Session } from 'next-auth'
import { signOut } from 'next-auth/react'
import Link from 'next/link'
import { Icon } from '@/components/icon'
import { faChevronDown } from '@fortawesome/free-solid-svg-icons'

interface UserButtonProps {
  firstName: string
  lastName: string
  role: string
  orgName: string
  setFileUploadModal: React.Dispatch<React.SetStateAction<boolean>>
}

export function UserButton({
  firstName,
  lastName,
  role,
  orgName,
  setFileUploadModal
}: UserButtonProps) {
  const { session } = useAuthContext()
  const canUploadFiles = useUploadableDocumentTypes().length > 0

  if (!session || !firstName || !lastName || !role || !orgName)
    return <Loader size="sm" />

  return (
    <Menu shadow="md" width={200}>
      <Menu.Target>
        <Button
          data-testid="user-menu-trigger"
          variant="transparent"
          c="white"
          rightSection={<Icon icon={faChevronDown} size={10} />}
        >
          {firstName} {lastName}
        </Button>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>
          {role} @ {orgName}
        </Menu.Label>
        <Menu.Divider />
        <Menu.Item
          component={Link}
          href="/profile"
          className={dropdownItemClass}
        >
          Profile
        </Menu.Item>
        <Menu.Item component={Link} href="/tasks" className={dropdownItemClass}>
          Tasks
        </Menu.Item>

        {canUploadFiles && (
          <Menu.Item
            className="flex w-[100%] rounded-md text-sm font-medium whitespace-nowrap text-[var(--mantine-color-celerityBrand-9)] hover:text-[var(--mantine-color-celerityBrand-5)]"
            onClick={() => setFileUploadModal(true)}
          >
            Upload a File
          </Menu.Item>
        )}

        <Menu.Item
          className="flex w-[100%] rounded-md text-sm font-medium whitespace-nowrap text-[var(--mantine-color-celerityBrand-9)] hover:text-[var(--mantine-color-celerityBrand-5)]"
          onClick={() => logout(session)}
        >
          Sign Out
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  )
}

const logout = async (session: Session | undefined) => {
  // Show the loading state instead of the sign-in page while we tear down and
  // redirect to Cognito — avoids a broken-logo flash from the aborted image
  // request on the momentarily-rendered sign-in page.
  logoutState.active = true

  // Clear session-scoped localStorage. Iterate over a snapshot of keys
  // because removing during a length-indexed loop skips entries.
  const sessionId = session?.sessionId ?? ''
  if (sessionId) {
    Object.keys(localStorage)
      .filter((key) => key.includes(sessionId))
      .forEach((key) => localStorage.removeItem(key))
  }
  localStorage.removeItem('impactReportCriteria')
  localStorage.removeItem('hasLoggedIn')

  const cognitoDomain = process.env.NEXT_PUBLIC_COGNITO_DOMAIN ?? ''
  const clientId = process.env.NEXT_PUBLIC_AUTH_APP_CLIENT_ID ?? ''
  const appUrl = encodeURIComponent(process.env.NEXT_PUBLIC_APP_URL ?? '')
  const cognitoLogoutUrl = `${cognitoDomain}/logout?client_id=${clientId}&scope=openid&response_type=code&logout_uri=${appUrl}&redirect_uri=${appUrl}`

  // Clear the NextAuth session first without redirecting — NextAuth's
  // default redirect callback rejects external URLs (Cognito's domain is
  // off-origin), so passing callbackUrl here silently drops the redirect
  // and leaves the AWS session alive. Navigate manually instead.
  await signOut({ redirect: false })

  window.location.href = cognitoLogoutUrl
}
